import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api, type DailyStats, type ReportResponse } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney, formatPips } from "@/lib/format";

/**
 * M12g — the reports that get posted to Telegram, readable after the fact and steppable
 * backwards.
 *
 * `/api/reports` shipped with M12e and had no consumer until now: the numbers behind every
 * daily, weekly and monthly message existed and were reachable only with curl.
 *
 * **This is a calendar period, not a trailing window.** The Dashboard's "Today" card asks
 * `/api/stats/overview` — "how are we doing lately" — and this screen asks "what did the
 * report for that week actually say". The server keeps those two questions apart on purpose,
 * and answering both with one range would give a wrong answer to one of them.
 *
 * **It reads fresh rather than replaying the stored message**, so a day the trailing re-roll
 * has since corrected shows the correction. That means this screen can legitimately disagree
 * with the message sitting in the trades channel — and that is the honest direction to
 * disagree in: the message is a dated document, this is the current state of the same days.
 * The screen says so rather than leaving Andy to discover it.
 *
 * The three rendering rules are M12f's, unchanged, because they are one rule: a rate carries
 * its denominator, null renders as a dash and never as zero, and unclassified closes are
 * shown rather than folded into losses.
 */

const PERIODS = ["daily", "weekly", "monthly"] as const;
type Period = (typeof PERIODS)[number];

const PERIOD_LABEL: Record<Period, string> = { daily: "Day", weekly: "Week", monthly: "Month" };
/** The route's own ceiling — asking for more is a 400, so the UI stops here and says why. */
const MAX_OFFSET = 52;

export function Reports() {
  const [period, setPeriod] = useState<Period>("daily");
  const [offset, setOffset] = useState(0);

  const query = useQuery({
    queryKey: queryKeys.report(period, offset),
    queryFn: () => api.report(period, offset),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((option) => (
          <Button
            key={option}
            size="sm"
            variant={option === period ? "default" : "outline"}
            onClick={() => {
              setPeriod(option);
              // Offset counts periods, so keeping "3 back" across a daily → monthly switch
              // would silently jump three months. Back to the most recent one.
              setOffset(0);
            }}
          >
            {PERIOD_LABEL[option]}
          </Button>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset >= MAX_OFFSET}
          onClick={() => setOffset((current) => Math.min(MAX_OFFSET, current + 1))}
        >
          <ChevronLeft className="h-4 w-4" /> Older
        </Button>
        <div className="text-center text-sm">
          {query.data ? (
            <>
              <div className="font-medium">
                {query.data.from} → {query.data.to}
              </div>
              <div className="text-xs text-muted-foreground">
                {offset === 0 ? `most recent ${PERIOD_LABEL[period].toLowerCase()}` : `${offset} back`}
              </div>
            </>
          ) : (
            <span className="text-muted-foreground">…</span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => setOffset((current) => Math.max(0, current - 1))}
        >
          Newer <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {offset >= MAX_OFFSET && (
        <p className="text-xs text-muted-foreground">
          This is as far back as the reports go — the API stops at {MAX_OFFSET} periods.
        </p>
      )}

      {/* Error before loading: a failed query never delivers data, so a loading-first guard
          would render a skeleton that never resolves and "broken" would look like "slow". */}
      {query.isError ? (
        <p className="text-sm text-destructive">Could not load this report. Nothing else is affected.</p>
      ) : query.isLoading || !query.data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ReportBody report={query.data} />
      )}

      <SpendChartCard />
    </div>
  );
}

/**
 * M15 — daily provider spend over the last 30 days: model spend from our own metered figures,
 * MetaApi from day-over-day balance deltas (so it only fills in as snapshots accumulate).
 * Hand-rolled SVG rather than a chart dependency: two polylines and a dollar scale is all
 * this needs.
 */
function SpendChartCard() {
  const history = useQuery({
    queryKey: queryKeys.creditsHistory(30),
    queryFn: () => api.creditsHistory(30),
  });

  const days = history.data?.days ?? [];
  const hasAny = days.some((d) => d.llmSpentUsd !== null || d.metaapiSpentUsd !== null);

  const width = 320;
  const height = 96;
  const max = Math.max(
    0.01,
    ...days.map((d) => Math.max(d.llmSpentUsd ?? 0, d.metaapiSpentUsd ?? 0)),
  );
  const x = (i: number) => (days.length <= 1 ? 0 : (i / (days.length - 1)) * width);
  const y = (v: number) => height - (v / max) * (height - 8);

  const line = (pick: (d: (typeof days)[number]) => number | null) =>
    days
      .map((d, i) => {
        const v = pick(d);
        return v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      })
      .filter(Boolean)
      .join(" ");

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-baseline justify-between">
          <div className="text-sm font-medium">Credit spend — last 30 days</div>
          <div className="text-xs text-muted-foreground">peak ${max.toFixed(2)}/day</div>
        </div>
        {history.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : !hasAny ? (
          <p className="text-xs text-muted-foreground">
            No spend recorded yet — the series fills in as daily briefings run.
          </p>
        ) : (
          <>
            <svg viewBox={`0 0 ${width} ${height}`} className="h-24 w-full" preserveAspectRatio="none">
              <polyline points={line((d) => d.llmSpentUsd)} fill="none" stroke="#22c55e" strokeWidth="1.5" />
              <polyline points={line((d) => d.metaapiSpentUsd)} fill="none" stroke="#3b82f6" strokeWidth="1.5" />
            </svg>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                {/* "Models", not a vendor name: this line is continuous across the M36
                    switch, because what it measures did not change when the vendor did. */}
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#22c55e" }} /> Models
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#3b82f6" }} /> MetaApi
              </span>
              <span>{days[0]?.day} → {days[days.length - 1]?.day}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ReportBody({ report }: { report: ReportResponse }) {
  const account = report.rows.find((row) => row.channelId === null) ?? null;
  const channels = report.rows.filter((row) => row.channelId !== null);
  // The provisional day has no `stats_daily` row by design, so it is not one of the days
  // this figure is out of.
  const settledDays = report.totalDays - (report.provisionalDay === null ? 0 : 1);

  return (
    <div className="space-y-4">
      <CoverageCard report={report} settledDays={settledDays} />

      {account === null ? (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            Nothing closed in this period. That is a real answer, not a missing one — no signal
            reached a take-profit or a stop between these dates.
          </CardContent>
        </Card>
      ) : (
        <StatsCard title="Everything" stats={account} currency={report.currency} />
      )}

      {channels.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            By channel
          </div>
          {channels.map((row) => (
            <StatsCard
              key={row.channelId}
              title={row.channelTitle ?? `Channel #${row.channelId}`}
              stats={row}
              currency={report.currency}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        These figures are read fresh, not copied from the message that was posted at the time —
        so if a day has been corrected since, this shows the correction and the message in the
        trades channel will not.
      </p>
    </div>
  );
}

/**
 * How many of the period's days have anything recorded against them.
 *
 * **This number cannot tell a quiet day from a missing one, and says so.** `rollupDay` writes
 * no `stats_daily` row at all for a day on which nothing closed, so a weekend, a week with no
 * signals and a week the app was switched off all produce the same count. An earlier draft of
 * this card called the gap "days with no rolled-up row… usually a day the app was not
 * running", which was simply false — most gaps are Saturdays.
 *
 * It is still worth showing, for the same reason the weekly report carries its own
 * `daysCovered`: a total drawn from four days out of seven is a different claim from a total
 * drawn from seven, whichever the reason.
 */
function CoverageCard({ report, settledDays }: { report: ReportResponse; settledDays: number }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-4 text-xs">
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Days in this period</span>
          <span className="tabular-nums">{report.totalDays}</span>
        </div>
        <div className="flex justify-between gap-2">
          <span className="text-muted-foreground">Days with anything recorded</span>
          <span className="tabular-nums">
            {report.daysRolledUp} of {settledDays}
          </span>
        </div>
        {report.daysRolledUp < settledDays && (
          <p className="pt-1 text-muted-foreground">
            The other {settledDays - report.daysRolledUp} had nothing close — which is a weekend,
            a quiet stretch, or a day this app was not running. Nothing here can tell those
            apart, so it does not guess.
          </p>
        )}
        {report.provisionalDay !== null && (
          <p className="pt-1 text-muted-foreground">
            {report.provisionalDay} is today and is counted live — the nightly rollup has not
            covered it yet, so these numbers can still move.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function StatsCard({ title, stats, currency }: { title: string; stats: DailyStats; currency: string }) {
  const decided = stats.wins + stats.losses;

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{title}</div>
            <div className="text-xs text-muted-foreground">{stats.signalsClosed} closed</div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold tabular-nums">
              {formatMoney(stats.profitMoney, currency)}
            </div>
            <div className="text-xs text-muted-foreground">
              {/* The denominator travels with the rate everywhere in this app. */}
              {decided === 0 ? "—" : `${Math.round((stats.wins / decided) * 100)}% of ${decided} decided`}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="outline">TP1 {stats.tp1Hits}</Badge>
          <Badge variant="outline">TP2 {stats.tp2Hits}</Badge>
          <Badge variant="outline">TP3 {stats.tp3Hits}</Badge>
          <Badge variant="outline">SL {stats.slHits}</Badge>
          <Badge variant="outline">BE {stats.breakeven}</Badge>
          {stats.unclassified > 0 && <Badge variant="secondary">{stats.unclassified} unclassified</Badge>}
          {stats.foulCount > 0 && <Badge variant="destructive">🚩 {stats.foulCount}</Badge>}
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Ours</div>
            <div className="tabular-nums">{formatPips(stats.pipsRealized)}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Theirs</div>
            <div className="tabular-nums">{formatPips(stats.pipsClaimed)}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
